import { Component, Injectable } from '@angular/core';
import { SettingsService } from '../../service/settings.service';
import { TridysService } from '../../service/tridys.service';
import { CommentsService } from '../../service/comments.service';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColDef, RowClickedEvent, SelectionColumnDef, iconSetQuartzLight, themeQuartz } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE } from '@ag-grid-community/locale';
import { compareGridText } from '../../shared/grid-text-comparator';
import { TridyQrExportDialogComponent } from '../tridy-qr-export-dialog/tridy-qr-export-dialog.component';
import { DppUrlService } from '../../service/dpp-url.service';

@Injectable()
export class MyCustomPaginatorIntl implements MatPaginatorIntl {
  changes = new Subject<void>();
  constructor(private translate: TranslateService){}

  firstPageLabel = this.translate.instant("first_page");
  itemsPerPageLabel = this.translate.instant("items_page");
  lastPageLabel =this.translate.instant("last_page")
  nextPageLabel = this.translate.instant("next_page");
  previousPageLabel = this.translate.instant("previous_page");

  getRangeLabel(page: number, pageSize: number, length: number): string {
    if (length === 0) {
      return this.translate.instant("page1_1");
    }
    const amountPages = Math.ceil(length / pageSize);
     
    return this.translate.instant("page_of_1") + (page + 1) +this.translate.instant("page_of_2") + amountPages;
  }
}
@Component({
    selector: 'app-tridys',
    templateUrl: './tridys.component.html',
    styleUrls: ['./tridys.component.css'],
    providers: [{ provide: MatPaginatorIntl, useClass: MyCustomPaginatorIntl }],
    standalone: false
})

export class TridysComponent {
  tridysData: any[] = [];
  selectedTridys: any[] = [];
  contentTypes: any[] = [];
  totalTridys = 0;
  isLoadingTridys = false;
  hasMoreTridys = false;
  private lastTridyPageDoc: any;
  private readonly tridyPageSize = 100;
  readonly paginationPageSizeSelector = [25, 50, 100];
  readonly agGridLocaleText = AG_GRID_LOCALE_DE;
  readonly autoSizeStrategy: any = { type: 'fitGridWidth' };
  readonly rowSelection: any = { mode: 'multiRow', checkboxes: true, headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false };
  readonly selectionColumnDef: SelectionColumnDef = {
    width: 56,
    resizable: false,
    cellStyle: { display: 'flex', alignItems: 'center' }
  };
  readonly dateFilterParams = {
    browserDatePicker: true,
    buttons: ['reset', 'apply'],
    closeOnApply: true,
    comparator: (filterDate: Date, cellValue: any) => this.compareDateFilter(filterDate, cellValue)
  };
  quickFilterText = '';
  readonly theme = themeQuartz.withPart(iconSetQuartzLight).withParams({
    backgroundColor: '#ffffff',
    headerBackgroundColor: '#f8fafc',
    foregroundColor: 'rgb(46, 55, 66)',
    borderColor: '#e4eaf1',
    oddRowBackgroundColor: '#fbfcfe',
    headerFontSize: 14,
    headerFontWeight: 700,
    rowHoverColor: '#fff7f3',
    selectedRowBackgroundColor: '#fff1eb',
    rowBorder: true,
    wrapperBorder: false,
    wrapperBorderRadius: 8,
    fontFamily: 'Roboto, sans-serif'
  });

  columnDefs: ColDef[] = [];

  defaultColDef: ColDef = {
    sortable: true,
    comparator: compareGridText,
    resizable: true,
    filter: true,
    lockVisible: true,
    lockPinned: true,
    mainMenuItems: params => params.defaultItems.filter(item => item !== 'columnChooser' && item !== 'pinSubMenu')
  };

  constructor( private  settingsService: SettingsService,
    private tridysService: TridysService,
    private commentService: CommentsService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private dppUrlService: DppUrlService,
    private translate: TranslateService ){ }
  breakpoint: number;
  paginatorSize = this.settingsService.paginator$

  get canDeleteTridys(): boolean {
    const contextRoles = this.settingsService.userRolesPerContext$?.[this.settingsService.contextId$] || [];
    const globalRoles = this.settingsService.userRoles$ || [];
    return contextRoles.includes('admin') || globalRoles.includes('admin');
  }

  ngOnInit(): void {
    this.breakpoint = (window.innerWidth <= 400) ? 1 : 6;
    this.setColumnDefs();
    this.contentTypes = this.settingsService.settingsContentTypesWithId || [];
    if (!this.contentTypes.length) {
      this.settingsService.observeContextsContentTypesFirestoreWithId(this.settingsService.contextId$)
        .subscribe(contentTypes => {
          this.contentTypes = contentTypes || [];
          this.refreshTridyRows();
        });
    }
    this.loadTridys(true);
  }

  applyFilter(event: Event) {
    this.quickFilterText = (event.target as HTMLInputElement).value.trim();
  }

  openGenerateTridys(): void {
    this.router.navigate(['home', this.settingsService.contextId$, 'generate-tridys']);
  }

  openTridy(event: RowClickedEvent): void {
    const target = event.event?.target as HTMLElement | undefined;
    if (target?.closest?.('.ag-selection-checkbox, .ag-checkbox-input-wrapper')) return;
    if (event.data?.id) this.router.navigate([event.data.id], { relativeTo: this.route });
  }

  onSelectionChanged(event: any): void {
    this.selectedTridys = event.api.getSelectedRows() || [];
  }

  openQrExportDialog(): void {
    if (!this.selectedTridys.length) return;
    this.dialog.open(TridyQrExportDialogComponent, {
      width: '760px',
      maxWidth: '96vw',
      data: {
        title: 'Download selected QR codes',
        filenamePrefix: 'dpp-qr-codes',
        contentTypes: this.contentTypes.filter(contentType => this.selectedTridys.some(tridy => tridy.contentType === contentType.id)),
        tridys: this.selectedTridys.map(tridy => ({
          id: tridy.id,
          payload: tridy.payload || {},
          nfcURLPrefix: this.getContentTypePrefix(tridy.contentType)
        }))
      }
    });
  }

  async deleteSelectedTridys(): Promise<void> {
    if (!this.canDeleteTridys || !this.selectedTridys.length) return;
    const count = this.selectedTridys.length;
    const confirmed = window.confirm(this.translate.instant(count === 1 ? 'tridys.confirmDeleteOne' : 'tridys.confirmDeleteMany', { count }));
    if (!confirmed) return;

    try {
      this.commentService.progressSpin.emit(true);
      const ids = this.selectedTridys.map(tridy => this.getTridyId(tridy)).filter(Boolean);
      await Promise.all(ids.map(id => this.tridysService.deleteTridy(id)));
      this.tridysData = this.tridysData.filter(tridy => !ids.includes(this.getTridyId(tridy)));
      this.totalTridys = Math.max(0, this.totalTridys - ids.length);
      this.hasMoreTridys = this.tridysData.length < this.totalTridys;
      this.selectedTridys = [];
      this.commentService.addSnackBar.emit(this.translate.instant(count === 1 ? 'tridys.deletedOne' : 'tridys.deletedMany', { count }));
    } catch (error) {
      console.log(error);
      this.commentService.addSnackBar.emit(this.translate.instant('tridys.deleteFailed'));
    } finally {
      this.commentService.progressSpin.emit(false);
    }
  }

  async loadMoreTridys(): Promise<void> {
    await this.loadTridys(false);
  }

  private async loadTridys(reset = false): Promise<void> {
    if (this.isLoadingTridys) return;

    try {
      this.isLoadingTridys = true;
      this.commentService.progressSpin.emit(true);
      if (reset) {
        this.tridysData = [];
        this.selectedTridys = [];
        this.lastTridyPageDoc = undefined;
        this.totalTridys = 0;
        this.hasMoreTridys = false;
      }

      const page = await this.tridysService.getTridysPage(this.tridyPageSize, this.lastTridyPageDoc);
      const nextRows = (page.items || []).map(value => this.toTridyRow(value));
      const rowsById = new Map(this.tridysData.map(row => [this.getTridyId(row), row]));
      nextRows.forEach(row => rowsById.set(this.getTridyId(row), row));

      this.tridysData = Array.from(rowsById.values());
      this.lastTridyPageDoc = page.lastDoc || this.lastTridyPageDoc;
      this.totalTridys = page.total;
      this.hasMoreTridys = this.tridysData.length < this.totalTridys && !!page.lastDoc;
    } catch (error) {
      console.log(error);
      this.commentService.addSnackBar.emit(this.translate.instant('tridys.loadFailed'));
    } finally {
      this.isLoadingTridys = false;
      this.commentService.progressSpin.emit(false);
    }
  }

  private getContentTypePrefix(contentTypeId: string): string | undefined {
    return this.contentTypes.find(contentType => contentType.id === contentTypeId)?.nfcURLPrefix;
  }

  private setColumnDefs(): void {
    this.columnDefs = [
      { field: 'contentName', headerName: this.t('contentTypes.template', 'Vorlage'), minWidth: 220, flex: 1, filter: 'agTextColumnFilter', valueFormatter: params => params.value || '—' },
      { field: 'id', headerName: this.t('id', 'ID'), minWidth: 190, flex: 1, filter: 'agTextColumnFilter' },
      { field: 'tridyUrl', headerName: this.t('url', 'URL'), minWidth: 280, flex: 1.4, hide: false, filter: 'agTextColumnFilter', cellRenderer: params => this.renderTridyUrl(params.value) },
      { field: 'sourceName', headerName: this.t('source', 'Quelle'), minWidth: 220, flex: 1, filter: 'agTextColumnFilter', valueFormatter: params => params.value || '—' },
      { field: 'creationDate', headerName: this.t('creation_date', 'Erstellt am'), width: 165, filter: 'agDateColumnFilter', filterParams: this.dateFilterParams, cellDataType: false, comparator: (left, right) => this.compareDates(left, right), valueFormatter: params => this.formatDate(params.value) },
      { field: 'timeStamp', headerName: this.t('last_update', 'Geändert am'), width: 165, sort: 'desc', filter: 'agDateColumnFilter', filterParams: this.dateFilterParams, cellDataType: false, comparator: (left, right) => this.compareDates(left, right), valueFormatter: params => this.formatDate(params.value) }
    ];
  }

  private t(key: string, fallback: string): string {
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : fallback;
  }

  private refreshTridyRows(): void {
    this.tridysData = this.tridysData.map(tridy => ({
      ...tridy,
      contentName: this.getContentTypeName(tridy.contentType) || tridy.contentName,
      tridyUrl: this.buildTridyUrl(tridy)
    }));
  }

  private toTridyRow(value: any): any {
    const id = this.getTridyId(value);
    const row = {
      ...value,
      id,
      contentName: this.getContentTypeName(value.contentType) || value.contentName || value.contentType || '',
      sourceName: value.sourceFileName || value.source?.fileName || '',
    };
    return {
      ...row,
      tridyUrl: this.buildTridyUrl(row)
    };
  }

  private buildTridyUrl(tridy: any): string {
    const tridyId = this.getTridyId(tridy);
    return tridyId ? this.dppUrlService.getTridyUrl(tridyId, this.getContentTypePrefix(tridy.contentType)) : '';
  }

  private getTridyId(tridy: any): string {
    return tridy?.id || tridy?.tridyId || tridy?.docId || tridy?.documentId || '';
  }

  private getContentTypeName(contentTypeId: string): string {
    const contentType = this.contentTypes.find(contentType => contentType.id === contentTypeId);
    return contentType?.displayName || contentType?.name || contentTypeId || '';
  }

  private renderTridyUrl(value: string): string {
    const url = this.escapeHtml(value || '');
    return url ? `<span class="grid-url" title="${url}">${url}</span>` : '<span class="grid-muted">—</span>';
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private formatDate(value: any): string {
    const date = this.getDateValue(value);
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : '';
  }

  private compareDates(left: any, right: any): number {
    return this.getTimestampMillis(left) - this.getTimestampMillis(right);
  }

  private compareDateFilter(filterDate: Date, cellValue: any): number {
    const cellDate = this.getDateValue(cellValue);
    if (!cellDate) return -1;
    const cellDateOnly = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()).getTime();
    const filterDateOnly = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate()).getTime();
    return cellDateOnly - filterDateOnly;
  }

  private getTimestampMillis(value: any): number {
    const date = this.getDateValue(value);
    const time = date?.getTime?.();
    return Number.isFinite(time) ? time : 0;
  }

  private getDateValue(value: any): Date | undefined {
    if (!value) return undefined;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (Number.isFinite(value?.seconds)) return new Date(value.seconds * 1000);
    return new Date(value);
  }

}
